import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/shared/contexts/AuthContext';
import { resolveRoles } from '@/shared/utils/previewRoles';
import LoadingSpinner from './LoadingSpinner';
import { isEmailVerified } from '@/shared/utils/emailVerified';

/**
 * Protected route wrapper that requires:
 * 1. Firebase authentication
 * 2. Email verification
 * 3. MongoDB profile exists
 * 4. Optionally, specific role(s)
 */
function ProtectedRoutes({ children, requiredRole }) {
    const {
        currentUser,
        userProfile,
        effectiveRoles,
        loading,
        hasProfile,
        profileLoading,
    } = useAuth();
    const location = useLocation();

    // Wait for auth to resolve. A refresh of a profile already held keeps the
    // page mounted: swapping in the spinner would discard the state of the
    // page that asked for the refresh, such as its success message.
    if (
        loading ||
        (profileLoading && hasProfile !== true) ||
        (isEmailVerified(currentUser) && hasProfile === null)
    ) {
        return <LoadingSpinner />;
    }

    // Not logged in — redirect to login
    if (!currentUser) {
        const redirectTo = location.pathname + location.search;
        return (
            <Navigate
                to={`/login?redirect=${encodeURIComponent(redirectTo)}`}
                replace
            />
        );
    }

    // Email not verified — redirect to login (which shows verification message)
    if (!isEmailVerified(currentUser)) {
        return <Navigate to="/login" replace />;
    }

    // No MongoDB profile — redirect to create-profile
    if (hasProfile === false && location.pathname !== '/create-profile') {
        return <Navigate to="/create-profile" replace />;
    }

    // Has profile but on create-profile page — redirect to dashboard
    if (hasProfile === true && location.pathname === '/create-profile') {
        return <Navigate to="/dashboard" replace />;
    }

    // Check role if required — deny when profile unavailable (no roles to check)
    if (requiredRole) {
        const roles = Array.isArray(requiredRole)
            ? requiredRole
            : [requiredRole];
        const userRoles = resolveRoles({ effectiveRoles, userProfile });
        const hasRequiredRole = roles.some((role) => userRoles.includes(role));

        // Admin bypasses role checks
        if (!hasRequiredRole && !userRoles.includes('admin')) {
            return <Navigate to="/dashboard" replace />;
        }
    }

    // Keyed by the account, so a page never carries one person's state, such
    // as a half-written note about a student, over to the next person signed
    // in in this tab. A refresh for the same account keeps it mounted.
    return <React.Fragment key={currentUser.uid}>{children}</React.Fragment>;
}

export default ProtectedRoutes;
